import React from 'react';
import KoobBadge from '@koob/ui/src/components/KoobBadge';
import KoobLoader from '@koob/ui/src/components/KoobLoader';
import ExternalFactsheetLink from '../ExternalFactsheetLink';
import TripDayCopyButton from '../TripDayCopyButton';
import type { RenderAccessAlert, Translate } from './types';

interface TripDayExperienceHeaderProps {
  experience: any;
  itineraryLabel?: string;
  canDuplicateExperience: boolean;
  onDuplicate: () => void;
  priceLoading: boolean;
  hasAvailabilityWarning: boolean;
  requiredMinPax?: number;
  isCurrencyMismatch: boolean;
  renderAccessAlert?: RenderAccessAlert;
  t: Translate;
}

export default function TripDayExperienceHeader({
  experience,
  itineraryLabel,
  canDuplicateExperience,
  onDuplicate,
  priceLoading,
  hasAvailabilityWarning,
  requiredMinPax,
  isCurrencyMismatch,
  renderAccessAlert,
  t,
}: TripDayExperienceHeaderProps) {
  const tooltip =
    experience?.isAccessible === false
      ? t('productAccess.productDescription')
      : requiredMinPax
        ? t('compose.minimumTravelersNotMet', { count: requiredMinPax })
        : t('compose.noAvailable');

  return (
    <>
      {canDuplicateExperience && (
        <TripDayCopyButton
          tipText={t('tooltip.duplicateExperience')}
          tipPosition={'left'}
          onClick={onDuplicate}
        />
      )}

      <div>
        {/* The duplicate control is absolutely positioned over the
            top-right corner of the card: it overlaps the text instead of
            pushing it, so the column reserves its width. */}
        <div
          className={`flex gap-2 ${canDuplicateExperience ? 'pr-7' : 'pr-1.5'}`}
        >
          <div className="w-1 shrink-0 rounded-full bg-orange-500" />

          <div>
            <div className="text-sm font-medium text-gray-900">
              <ExternalFactsheetLink name={experience?.name ?? ''} />
            </div>

            {itineraryLabel && (
              <div className="text-xs text-gray-500">{itineraryLabel}</div>
            )}

            {/* KD-3581 §9 / AC01 — the amounts belong to the frame
                header and are shown there only. The day keeps what is
                about its own state: the pricing request in flight, and
                the warning when the program cannot be priced. */}
            {(!experience?.dayIndex || experience.dayIndex === 1) && (
              <div className="flex justify-between pr-1.5">
                <div className="text-sm font-medium">
                  {priceLoading && (
                    <div className="my-1.5 pl-5">
                      <KoobLoader size={16} />
                    </div>
                  )}
                </div>

                <div className="absolute right-0">
                  {hasAvailabilityWarning &&
                    renderAccessAlert?.({ tooltip, isCurrencyMismatch })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {experience?.durationDays > 1 && (
        <div className="mt-1 flex justify-end">
          <KoobBadge color="orange">
            {experience.dayIndex}/{experience.durationDays}
          </KoobBadge>
        </div>
      )}
    </>
  );
}
